import React, { useState, useEffect } from 'react';
import { Shield, Plus, Trash2, RefreshCw, Globe, Ban, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api from '@/lib/api';

const IPRulesPage = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newIp, setNewIp] = useState('');
  const [newType, setNewType] = useState('blacklist');
  const [newNote, setNewNote] = useState('');

  useEffect(() => { fetchRules(); }, []);

  const fetchRules = async () => {
    try {
      setLoading(true);
      const r = await api.get('/admin/ip-rules');
      setRules(r.data || []);
    } catch { toast.error('Failed to load IP rules'); }
    finally { setLoading(false); }
  };

  const handleAdd = async () => {
    if (!newIp.trim()) { toast.error('IP address required'); return; }
    try {
      await api.post('/admin/ip-rules', { ip: newIp.trim(), rule_type: newType, note: newNote.trim() });
      toast.success(`IP ${newType === 'blacklist' ? 'blocked' : 'whitelisted'}`);
      setNewIp(''); setNewNote('');
      await fetchRules();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id, ip) => {
    if (!confirm(`Remove rule for ${ip}?`)) return;
    try {
      await api.delete(`/admin/ip-rules/${id}`);
      toast.success('Rule removed');
      await fetchRules();
    } catch { toast.error('Failed'); }
  };

  const blacklisted = rules.filter(r => r.rule_type === 'blacklist');
  const whitelisted = rules.filter(r => r.rule_type === 'whitelist');

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">IP Access Control</h1>
          <p className="text-slate-300">Manage IP whitelist and blacklist rules</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchRules} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Add Rule */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Plus className="h-5 w-5" /> Add Rule</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="text-sm font-medium mb-1 block text-slate-300">IP Address</label>
              <Input placeholder="e.g., 192.168.1.1" value={newIp} onChange={e => setNewIp(e.target.value)} className="w-48" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block text-slate-300">Type</label>
              <select value={newType} onChange={e => setNewType(e.target.value)} className="text-sm border rounded px-3 py-2 h-10">
                <option value="blacklist">Blacklist (Block)</option>
                <option value="whitelist">Whitelist (Allow)</option>
              </select>
            </div>
            <div className="flex-1">
              <label className="text-sm font-medium mb-1 block text-slate-300">Note (optional)</label>
              <Input placeholder="Reason or description" value={newNote} onChange={e => setNewNote(e.target.value)} />
            </div>
            <Button onClick={handleAdd} className="bg-[#DC2626] hover:bg-[#B91C1C]">
              <Plus className="h-4 w-4 mr-2" /> Add Rule
            </Button>
          </div>
          <p className="text-xs text-gray-400 mt-3">
            Blacklisted IPs are blocked from all API access. If any whitelist entries exist, ONLY whitelisted IPs are allowed.
          </p>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Blacklist */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Ban className="h-5 w-5 text-amber-400" /> Blacklist ({blacklisted.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {blacklisted.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">No blocked IPs</p>
            ) : (
              <div className="space-y-2">
                {blacklisted.map(rule => (
                  <div key={rule.id} className="flex items-center justify-between p-2 rounded border border-red-100 bg-amber-950/40">
                    <div>
                      <code className="text-sm font-mono text-amber-300">{rule.ip}</code>
                      {rule.note && <span className="text-xs text-gray-500 ml-2">— {rule.note}</span>}
                    </div>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-amber-400" onClick={() => handleDelete(rule.id, rule.ip)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Whitelist */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Check className="h-5 w-5 text-green-500" /> Whitelist ({whitelisted.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {whitelisted.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">No whitelisted IPs (all IPs allowed)</p>
            ) : (
              <div className="space-y-2">
                {whitelisted.map(rule => (
                  <div key={rule.id} className="flex items-center justify-between p-2 rounded border border-green-100 bg-emerald-950/40">
                    <div>
                      <code className="text-sm font-mono text-emerald-300">{rule.ip}</code>
                      {rule.note && <span className="text-xs text-gray-500 ml-2">— {rule.note}</span>}
                    </div>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-amber-400" onClick={() => handleDelete(rule.id, rule.ip)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default IPRulesPage;
